'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["previewViewportWidth", "previewViewportHeight", "isSidebarExpanded", "updatePreviewViewportDimensions", "previewWrapperScrollLeft", "updatePreviewWrapperScroll", "activeBreakpointName", "previewIframeLeftOffset"];
import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'underscore';
import { EDIT_PANE_CLASSNAME } from 'ContentEditorUI/constants/dom';
import { getPreviewIframeWrapperElement } from 'ContentEditorUI/lib/layoutDom';
import { getIsSidebarExpanded } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { getActiveBreakpointName } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { getPreviewViewportWidth, getPreviewViewportHeight, getPreviewWrapperScrollLeft, getPreviewIframeLeftOffset } from 'ContentEditorUI/redux/selectors/inpageSelectors';
import { updatePreviewViewportDimensions as updatePreviewViewportDimensionsAction, updatePreviewWrapperScroll as updatePreviewWrapperScrollAction } from 'ContentEditorUI/redux/actions/inpageActions';
import { jsx as _jsx } from "react/jsx-runtime";
const mapStateToProps = state => ({
  isSidebarExpanded: getIsSidebarExpanded(state),
  activeBreakpointName: getActiveBreakpointName(state),
  previewViewportWidth: getPreviewViewportWidth(state),
  previewViewportHeight: getPreviewViewportHeight(state),
  previewIframeLeftOffset: getPreviewIframeLeftOffset(state),
  previewWrapperScrollLeft: getPreviewWrapperScrollLeft(state)
});
const mapDispatchToProps = {
  updatePreviewViewportDimensions: updatePreviewViewportDimensionsAction,
  updatePreviewWrapperScroll: updatePreviewWrapperScrollAction
};

// This is a higher-order-component so it can be the _parent_ of ContentViewContainer, because it needs to know
// when the edit pane has been fully mounted (and that is easier by making it a parent instead of passing
// `hasMounted={true}` to a child component)
const PreviewViewportSizeHOC = WrappedComponent => {
  /**
   * @param {Object} props
   * @param {number} [props.previewViewportWidth] (optional)
   * @param {number} [props.previewViewportHeight] (optional)
   * @param {number} [props.previewWrapperScrollLeft] (optional)
   * @param {boolean} props.isSidebarExpanded (required)
   * @param {Function} props.updatePreviewViewportDimensions (required)
   * @param {Function} props.updatePreviewWrapperScroll (required)
   */
  const PreviewViewportSizeContainer = class extends Component {
    constructor(props) {
      super(props);
      this.editPaneRef = /*#__PURE__*/createRef();
      this.debouncedUpdatePreviewViewport = debounce(this.updatePreviewViewport.bind(this), 300);
      this.debouncedUpdatePreviewWrapperScroll = debounce(this.updatePreviewScroll.bind(this), 100);
    }
    componentDidMount() {
      var _this$editPaneRef$cur;
      this.editPane = (_this$editPaneRef$cur = this.editPaneRef.current) !== null && _this$editPaneRef$cur !== void 0 ? _this$editPaneRef$cur : document.getElementsByClassName(EDIT_PANE_CLASSNAME)[0];
      window.addEventListener('resize', this.debouncedUpdatePreviewViewport);
      this.debouncedUpdatePreviewViewport();
      this.previewIframeWrapperElement = getPreviewIframeWrapperElement();
      if (this.previewIframeWrapperElement) {
        this.previewIframeWrapperElement.addEventListener('scroll', this.debouncedUpdatePreviewWrapperScroll);
      }
      window.addEventListener('resize', this.debouncedUpdatePreviewWrapperScroll);
    }
    componentDidUpdate(prevProps) {
      const {
        isSidebarExpanded,
        activeBreakpointName
      } = this.props;
      const sidebarChanged = prevProps.isSidebarExpanded !== isSidebarExpanded;
      const breakpointChanged = prevProps.activeBreakpointName !== activeBreakpointName;
      if (sidebarChanged || breakpointChanged) {
        this.debouncedUpdatePreviewViewport();
      }
    }
    componentWillUnmount() {
      window.removeEventListener('resize', this.debouncedUpdatePreviewViewport);
      window.removeEventListener('resize', this.debouncedUpdatePreviewWrapperScroll);
      if (this.previewIframeWrapperElement) {
        this.previewIframeWrapperElement.removeEventListener('scroll', this.debouncedUpdatePreviewWrapperScroll);
      }
    }
    updatePreviewViewport() {
      const {
        previewViewportWidth,
        previewViewportHeight,
        previewIframeLeftOffset,
        updatePreviewViewportDimensions
      } = this.props;
      if (this.editPane) {
        const {
          top,
          left,
          width,
          height
        } = this.editPane.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const newWidth = windowWidth - left;
        const newHeight = windowHeight - top;
        const isPreviewVisible = width !== 0;
        const iframe = document.getElementById('contentEditor-preview');
        const newIframeLeftOffset = iframe ? width - iframe.getBoundingClientRect().width : 0;
        if (isPreviewVisible && (previewViewportWidth !== newWidth || previewViewportHeight !== newHeight || previewIframeLeftOffset !== newIframeLeftOffset)) {
          updatePreviewViewportDimensions({
            width,
            height,
            iframeLeftOffset: newIframeLeftOffset
          });
        }
      }
    }
    updatePreviewScroll() {
      const {
        previewWrapperScrollLeft,
        updatePreviewWrapperScroll
      } = this.props;
      if (this.previewIframeWrapperElement && this.editPane) {
        const {
          width: paneWidth
        } = this.editPane.getBoundingClientRect();
        const isPreviewVisible = paneWidth !== 0;
        const scrollLeft = this.previewIframeWrapperElement.scrollLeft;
        if (isPreviewVisible && previewWrapperScrollLeft !== scrollLeft) {
          updatePreviewWrapperScroll({
            scrollLeft
          });
        }
      }
    }
    render() {
      const _this$props = this.props,
        forwardedProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, forwardedProps, {
        editPaneRef: this.editPaneRef
      }));
    }
  };
  const ConnectedPreviewViewportSizeContainer = connect(mapStateToProps, mapDispatchToProps)(PreviewViewportSizeContainer);
  return ConnectedPreviewViewportSizeContainer;
};
export default PreviewViewportSizeHOC;